/**
 * SignInScreen — Passwordless email-OTP sign-in for MoneyMission.
 *
 * Flow:
 *   1. User enters email + completes Turnstile + (invisible honeypot must stay blank)
 *   2. We call supabase.auth.signInWithOtp({ email, options: { captchaToken } })
 *      → Supabase emails the user a 6-digit code via the Mailgun SMTP we configured
 *   3. UI flips to "Enter your code" — user types the 6 digits
 *   4. We call supabase.auth.verifyOtp({ email, token, type: 'email' })
 *      → On success the session is stored automatically; useAuth() updates everywhere
 *
 * Bot defenses (defense-in-depth):
 *   - Honeypot field (display:none) — bots fill in, humans don't. We reject if filled.
 *   - Cloudflare Turnstile widget — Supabase verifies the captchaToken server-side
 *     using the secret key Chris pasted in the Supabase dashboard.
 *
 * No passwords anywhere. shouldCreateUser: true → new emails auto-create an account.
 */

import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: { sitekey: string; callback: (token: string) => void; "error-callback"?: () => void; "expired-callback"?: () => void; theme?: string }) => string;
      remove: (id: string) => void;
      reset: (id?: string) => void;
    };
  }
}

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string;

type Step = "email" | "code" | "done";

interface Props {
  /** "self" = adult signing up for themselves. "parent" = adult setting up
   *  an account for a kid under 13 (parental-consent flow continues after). */
  mode?: "self" | "parent";
  /** Optional close handler. When omitted, the screen is mandatory (no ✕). */
  onClose?: () => void;
  onSignedIn?: () => void;
}

// Persist mid-flow state to sessionStorage so iOS Safari tab refreshes
// (which happen when the user switches to Mail.app to grab the OTP) don't
// lose the user's place. Scoped per-tab — cleared on done/email reset.
const SIGNIN_STATE_KEY = "mm_signin_inflight_v1";
type PersistedSignInState = { step: "code"; email: string; mode: "self" | "parent" } | null;

function loadPersistedSignIn(): PersistedSignInState {
  try {
    const raw = sessionStorage.getItem(SIGNIN_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.step === "code" && typeof parsed.email === "string" && parsed.email) {
      return parsed as PersistedSignInState;
    }
    return null;
  } catch { return null; }
}

function savePersistedSignIn(state: PersistedSignInState) {
  try {
    if (state) sessionStorage.setItem(SIGNIN_STATE_KEY, JSON.stringify(state));
    else sessionStorage.removeItem(SIGNIN_STATE_KEY);
  } catch { /* private mode / quota */ }
}

export default function SignInScreen({ mode = "self", onClose, onSignedIn }: Props) {
  const isParent = mode === "parent";

  // On first mount, restore mid-flow state if iOS reloaded us.
  const persisted = loadPersistedSignIn();
  const [step, setStep] = useState<Step>(persisted ? "code" : "email");
  const [email, setEmail] = useState(persisted?.email ?? "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(persisted ? "Welcome back — enter your code." : null);

  // Persist whenever we enter / leave the code step.
  useEffect(() => {
    if (step === "code" && email) savePersistedSignIn({ step: "code", email, mode });
    else savePersistedSignIn(null);
  }, [step, email, mode]);

  // Honeypot: hidden field that bots blindly fill. Humans never see it.
  const [honeypot, setHoneypot] = useState("");

  // Turnstile token (set by the Cloudflare widget's callback)
  const [tsToken, setTsToken] = useState<string | null>(null);
  const tsRef = useRef<HTMLDivElement>(null);
  const tsWidgetIdRef = useRef<string | null>(null);

  // Mount the Turnstile widget when on the email step
  useEffect(() => {
    if (step !== "email") return;
    if (!tsRef.current || !TURNSTILE_SITE_KEY) return;

    // Wait for the Turnstile script to load (it's loaded async/defer from index.html)
    let interval: number | null = null;
    function tryRender() {
      if (window.turnstile && tsRef.current && !tsWidgetIdRef.current) {
        tsWidgetIdRef.current = window.turnstile.render(tsRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => setTsToken(token),
          "error-callback": () => { setTsToken(null); setError("Bot check failed. Please retry."); },
          "expired-callback": () => setTsToken(null),
          theme: "light",
        });
        if (interval) { window.clearInterval(interval); interval = null; }
      }
    }
    tryRender();
    if (!tsWidgetIdRef.current) interval = window.setInterval(tryRender, 200);

    return () => {
      if (interval) window.clearInterval(interval);
      if (tsWidgetIdRef.current && window.turnstile) {
        window.turnstile.remove(tsWidgetIdRef.current);
        tsWidgetIdRef.current = null;
      }
    };
  }, [step]);

  async function handleSendCode(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null); setInfo(null);

    // Honeypot — bots fill, humans don't
    if (honeypot) { setError("Validation failed."); return; }

    // Strip ALL whitespace + the zero-width characters iOS autofill sometimes
    // injects (NBSP \u00A0, zero-width space \u200B, ZWNJ \u200C, ZWJ \u200D, BOM \uFEFF).
    // Then do a lenient structural check (one @, dot in domain, sane lengths).
    const cleaned = email
      .replace(/[\s\u00A0\u200B\u200C\u200D\uFEFF]+/g, "")
      .toLowerCase();
    const atParts = cleaned.split("@");
    const validShape =
      atParts.length === 2 &&
      atParts[0].length >= 1 &&
      atParts[1].includes(".") &&
      !atParts[1].endsWith(".") &&
      atParts[1].length >= 3;
    if (!validShape) {
      setError("Please enter a valid email address."); return;
    }
    const trimmed = cleaned;
    if (!tsToken) { setError("Please complete the bot check above."); return; }
    if (!supabase) { setError("Sign-in temporarily unavailable."); return; }

    setBusy(true);
    try {
      const { error: err } = await supabase.auth.signInWithOtp({
        email: trimmed,
        options: {
          shouldCreateUser: true,
          captchaToken: tsToken,
        },
      });
      if (err) {
        setError(err.message || "Couldn't send the code. Try again.");
      } else {
        setEmail(trimmed);
        setStep("code");
        setInfo("Check your email — code arrives in a few seconds.");
      }
    } catch (e: any) {
      setError(e?.message || "Network error. Try again.");
    } finally {
      setBusy(false);
      // Reset Turnstile so a fresh token is generated if user retries
      if (tsWidgetIdRef.current && window.turnstile) {
        window.turnstile.reset(tsWidgetIdRef.current);
        setTsToken(null);
      }
    }
  }

  async function handleVerifyCode(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null); setInfo(null);

    const cleanCode = code.replace(/\D/g, "");
    if (cleanCode.length !== 6) { setError("Enter the 6-digit code from your email."); return; }
    if (!supabase) return;

    setBusy(true);
    try {
      const { data, error: err } = await supabase.auth.verifyOtp({
        email,
        token: cleanCode,
        type: "email",
      });
      if (err) {
        setError(err.message || "That code didn't work. Try again, or request a new one.");
      } else if (data?.session) {
        setStep("done");
        if (onSignedIn) onSignedIn();
      }
    } catch (e: any) {
      setError(e?.message || "Network error. Try again.");
    } finally {
      setBusy(false);
    }
  }

  function startOver() {
    setStep("email"); setCode(""); setError(null); setInfo(null);
  }

  /* ─ Render ─────────────────────────────────────────────────────────────── */
  return (
    <div style={overlayStyle}>
      <div style={cardStyle}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ fontSize: 22, fontWeight: 900, color: "#0d9488", fontFamily: "Poppins" }}>
            MoneyMission
          </div>
          {onClose && (
            <button onClick={onClose} aria-label="Close"
              style={{ background: "none", border: "none", fontSize: 22, color: "#9ca3af", cursor: "pointer", padding: "8px 12px", minWidth: 44, minHeight: 44 }}>
              ×
            </button>
          )}
        </div>

        {step === "email" && (
          <form onSubmit={handleSendCode} noValidate>
            <h2 style={titleStyle}>
              {isParent ? "Parent / guardian sign in" : "Sign in"}
            </h2>
            <p style={subtitleStyle}>
              {isParent
                ? "Your child is under 13, so we need a parent or guardian to set up their account. Enter your email — we'll send a 6-digit code."
                : "No password needed. We'll email you a 6-digit code."}
            </p>

            <label htmlFor="email" style={labelStyle}>Email address</label>
            <input
              id="email" name="email" type="email" inputMode="email" autoComplete="email"
              value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required autoFocus
              style={inputStyle}
            />

            {/* Honeypot — display:none, bots fill, humans never see */}
            <input
              type="text" name="website" tabIndex={-1} autoComplete="off"
              value={honeypot} onChange={e => setHoneypot(e.target.value)}
              style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
              aria-hidden="true"
            />

            {/* Turnstile widget renders here */}
            <div style={{ marginTop: 16, marginBottom: 6, display: "flex", justifyContent: "center" }}>
              <div ref={tsRef} />
            </div>

            {error && <div style={errorStyle}>{error}</div>}

            <button type="submit" disabled={busy || !tsToken} style={primaryButtonStyle(busy || !tsToken)}>
              {busy ? "Sending…" : "Send me a code"}
            </button>

            <p style={fineprintStyle}>
              By signing in you agree to our{" "}
              <a href="https://moneymission.app/terms" target="_blank" rel="noopener noreferrer"
                style={{ color: "#0d9488" }}>Terms of Service</a>{" "}and{" "}
              <a href="https://moneymission.app/privacy" target="_blank" rel="noopener noreferrer"
                style={{ color: "#0d9488" }}>Privacy Policy</a>.
            </p>
          </form>
        )}

        {step === "code" && (
          <form onSubmit={handleVerifyCode} noValidate>
            <h2 style={titleStyle}>Enter your code</h2>
            <p style={subtitleStyle}>
              We sent a 6-digit code to <strong style={{ color: "#1f2937" }}>{email}</strong>.
            </p>

            <label htmlFor="code" style={labelStyle}>6-digit code</label>
            <input
              id="code" name="code" type="text" inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}" maxLength={6}
              value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              required autoFocus
              style={{ ...inputStyle, fontSize: 24, letterSpacing: 8, textAlign: "center", fontFamily: "ui-monospace, monospace" }}
            />

            {info && <div style={infoStyle}>{info}</div>}
            {error && <div style={errorStyle}>{error}</div>}

            <button type="submit" disabled={busy || code.length !== 6} style={primaryButtonStyle(busy || code.length !== 6)}>
              {busy ? "Verifying…" : "Sign in"}
            </button>

            <button type="button" onClick={startOver}
              style={{ background: "none", border: "none", color: "#6b7280", fontSize: 13, marginTop: 12, cursor: "pointer", padding: 8, width: "100%" }}>
              Use a different email or resend code
            </button>
          </form>
        )}

        {step === "done" && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>✓</div>
            <h2 style={titleStyle}>You're signed in</h2>
            <p style={subtitleStyle}>
              Welcome to MoneyMission. Closing this window…
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─ Styles ───────────────────────────────────────────────────────────────── */
const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 100,
  background: "rgba(15, 23, 42, 0.65)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
  paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
};

const cardStyle: React.CSSProperties = {
  background: "#fff",
  borderRadius: 24,
  padding: "24px 22px",
  maxWidth: 440,
  width: "100%",
  maxHeight: "calc(100vh - 32px)",
  overflowY: "auto",
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.25)",
};

const titleStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 900,
  color: "#1f2937",
  margin: "4px 0 6px",
  fontFamily: "Fredoka One",
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#6b7280",
  margin: "0 0 18px",
  lineHeight: 1.5,
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  fontWeight: 700,
  color: "#374151",
  textTransform: "uppercase",
  letterSpacing: 0.5,
  marginBottom: 6,
  marginTop: 8,
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "14px 16px",
  fontSize: 16,   // 16px floor prevents iOS Safari auto-zoom on focus
  border: "2px solid #99f6e4",
  borderRadius: 14,
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "Nunito, system-ui, sans-serif",
  fontWeight: 600,
  color: "#1f2937",
};

const errorStyle: React.CSSProperties = {
  marginTop: 12,
  padding: "10px 14px",
  background: "#fee2e2",
  color: "#991b1b",
  borderRadius: 12,
  fontSize: 13,
  fontWeight: 600,
  borderLeft: "4px solid #ef4444",
};

const infoStyle: React.CSSProperties = {
  marginTop: 12,
  padding: "10px 14px",
  background: "#d1fae5",
  color: "#065f46",
  borderRadius: 12,
  fontSize: 13,
  fontWeight: 600,
  borderLeft: "4px solid #10b981",
};

const fineprintStyle: React.CSSProperties = {
  fontSize: 11,
  color: "#9ca3af",
  marginTop: 16,
  lineHeight: 1.5,
  textAlign: "center",
};

function primaryButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    width: "100%",
    padding: 15,
    fontSize: 16,
    fontWeight: 800,
    background: disabled ? "#d1d5db" : "linear-gradient(135deg, #0d9488, #f59e0b)",
    color: "#fff",
    border: "none",
    borderRadius: 14,
    cursor: disabled ? "not-allowed" : "pointer",
    marginTop: 18,
    fontFamily: "Nunito, system-ui, sans-serif",
    minHeight: 48,
  };
}
